import { useState, useEffect } from "react";
import { Header, BottomNav } from "../components/Header";
import { ProductCard } from "../components/ProductCard";
import { Input } from "../components/ui/input";
import axios from "axios";
import { API } from "../App";
import { Search, X, ArrowLeft, SlidersHorizontal } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CATEGORIES, normalizeCategory } from "../lib/categories";

const SearchPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAllProducts();
  }, []);

  const fetchAllProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setAllProducts(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoading(false);
    }
  };

  const products = allProducts.filter((p) => {
    if (query.trim()) {
      const q = query.toLowerCase();
      if (!p.title.toLowerCase().includes(q) && !p.description.toLowerCase().includes(q)) return false;
    }
    if (selectedCategory !== "all" && normalizeCategory(p.category) !== selectedCategory) return false;
    const price = p.current_bid || p.price;
    if (minPrice !== "" && price < parseFloat(minPrice)) return false;
    if (maxPrice !== "" && price > parseFloat(maxPrice)) return false;
    return true;
  });

  const hasActiveFilters = selectedCategory !== "all" || minPrice !== "" || maxPrice !== "";

  const clearFilters = () => {
    setSelectedCategory("all");
    setMinPrice("");
    setMaxPrice("");
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] pb-20 md:pb-8">
      <Header />

      <main className="max-w-[470px] mx-auto px-4 py-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[#64748B] hover:text-[#0F172A] mb-6"
          data-testid="back-button"
        >
          <ArrowLeft size={20} />
          Volver
        </button>

        {/* Search Input */}
        <div className="relative mb-3">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#64748B]" size={20} />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar productos..."
            className="pl-12 pr-20 h-12 rounded-full border-[#E2E8F0] bg-white"
            data-testid="search-input"
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {query && (
              <button
                onClick={() => setQuery("")}
                className="p-1 text-[#64748B] hover:text-[#0F172A]"
              >
                <X size={18} />
              </button>
            )}
            <button
              onClick={() => setShowFilters((v) => !v)}
              className={`p-1.5 rounded-full transition-colors ${
                showFilters || hasActiveFilters ? "bg-[#475569] text-white" : "text-[#64748B] hover:text-[#0F172A]"
              }`}
              data-testid="toggle-filters-btn"
            >
              <SlidersHorizontal size={16} />
            </button>
          </div>
        </div>

        {/* Category chips - always visible */}
        <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2 mb-2 -mx-4 px-4" data-testid="category-filter-chips">
          <button
            onClick={() => setSelectedCategory("all")}
            className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              selectedCategory === "all"
                ? "bg-[#475569] text-white border-[#475569]"
                : "bg-white text-[#64748B] border-[#E2E8F0]"
            }`}
            data-testid="filter-category-all"
          >
            Todas
          </button>
          {CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                selectedCategory === cat.id
                  ? "bg-[#475569] text-white border-[#475569]"
                  : "bg-white text-[#64748B] border-[#E2E8F0]"
              }`}
              data-testid={`filter-category-${cat.id}`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Price range filter */}
        {showFilters && (
          <div className="bg-white rounded-2xl border border-[#E2E8F0] p-4 mb-4" data-testid="price-filter-panel">
            <p className="text-xs font-semibold text-[#0F172A] mb-2">Rango de precio ($)</p>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min="0"
                value={minPrice}
                onChange={(e) => setMinPrice(e.target.value)}
                placeholder="Mín"
                className="h-10 rounded-xl border-[#E2E8F0]"
                data-testid="min-price-input"
              />
              <span className="text-[#94A3B8]">—</span>
              <Input
                type="number"
                min="0"
                value={maxPrice}
                onChange={(e) => setMaxPrice(e.target.value)}
                placeholder="Máx"
                className="h-10 rounded-xl border-[#E2E8F0]"
                data-testid="max-price-input"
              />
            </div>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="mt-3 text-xs text-[#3B82F6] font-medium"
                data-testid="clear-filters-btn"
              >
                Limpiar filtros
              </button>
            )}
          </div>
        )}

        {/* Results */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner"></div>
          </div>
        ) : products.length === 0 ? (
          <div className="empty-state" data-testid="no-results">
            <Search size={64} />
            <h3 className="text-lg font-semibold text-[#0F172A] mt-4">
              {query || hasActiveFilters ? "No se encontraron resultados" : "No hay productos"}
            </h3>
            <p className="text-sm text-[#64748B]">
              {query || hasActiveFilters ? "Intenta con otra búsqueda o ajusta los filtros" : "Sé el primero en publicar"}
            </p>
          </div>
        ) : (
          <>
            {(query || hasActiveFilters) && (
              <p className="text-sm text-[#64748B] mb-4" data-testid="results-count">
                {products.length} resultado{products.length !== 1 ? "s" : ""}
                {query ? ` para "${query}"` : ""}
              </p>
            )}
            <div className="space-y-6" data-testid="search-results">
              {products.map((product) => (
                <ProductCard key={product.product_id} product={product} />
              ))}
            </div>
          </>
        )}
      </main>

      <BottomNav />
    </div>
  );
};

export default SearchPage;
